import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import CircleIcon from '@mui/icons-material/Circle';
import LastPageIcon from '@mui/icons-material/LastPage';
import { Box, IconButton, Tab, Tabs, Typography } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import { makeStyles } from '@mui/styles';
import {
  Estate,
  EstateInputDataFormatType,
  EstateType,
  EstateWorkItem,
  useDocument_GetFilesQuery,
  useEstateInputDataInstance_GetEstateInputDataInstancesByEstateWorkItemIdQuery,
  useEstateWorkItem_GetEstateWorkItemByIdQuery,
} from 'queries';

import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import AssignedTaskActivity from './assigned-task-activity';
import AssignedTaskDocuments from './assigned-task-documents';
import AssignedTaskMatterInfo from './assigned-task-matter-info';
import AssignedTaskNotes from './assigned-task-notes';

const useStyles = makeStyles(() => ({
  container: {
    padding: '20px',
    color: '#1C2541',
  },
  header: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
  },
  tabsContainer: {
    borderBottom: '1px solid #ECEEF1',
    marginBottom: '8px',
  },
  lastPageIcon: {
    color: '#1C2541',
  },
  actionNeededCircleIcon: {
    color: 'red',
    height: 12,
    width: 12,
  },
  hidden: {
    display: 'none',
  },
}));

type Props = {
  estateWorkItemId: string;
  estate: Estate;
  onClose: () => void;
};

enum AssignedTaskDrawerTabs {
  TASK_ACTIVITY = 0,
  MATTER_INFORMATION = 1,
  DOCUMENTS = 2,
  NOTES = 3,
}

const AssignedTaskDrawer = ({ estateWorkItemId, estate, onClose }: Props) => {
  const { portalId } = useParams<Record<string, string>>();
  const [currentTab, setCurrentTab] = useState(AssignedTaskDrawerTabs.TASK_ACTIVITY);
  const classes = useStyles();

  const { data: estateWorkItemData, loading: isEstateWorkItemDataLoading } =
    useEstateWorkItem_GetEstateWorkItemByIdQuery({
      variables: {
        input: {
          estateWorkItemId,
          portalId,
        },
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting estate work item by ID: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const {
    data: getFilesData,
    loading: isGetFilesDataLoading,
    refetch: refetchGetFilesData,
  } = useDocument_GetFilesQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        portalId,
        parentId: estate.documentsFolderId,
        estateWorkItemId: estateWorkItemId,
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting documents: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const {
    data: estateInputDataInstancesData,
    loading: estateInputDataInstancesLoading,
    refetch: refetchEstateInputDataInstances,
  } = useEstateInputDataInstance_GetEstateInputDataInstancesByEstateWorkItemIdQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        estateWorkItemId,
        portalId,
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting estate input data instances by estate work item ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const isLoading =
    !estateInputDataInstancesData ||
    estateInputDataInstancesLoading ||
    isGetFilesDataLoading ||
    isEstateWorkItemDataLoading;

  const estateInputDataInstances = estateInputDataInstancesData?.data || [];

  function getIsMatterInfoRequiredFieldPending() {
    let isPending = false;
    if (estate.type === EstateType.Truadmin) {
      isPending =
        !estate.estateTruadminFields?.decedentFirstName ||
        !estate.estateTruadminFields?.decedentLastName;
    } else if (estate.type === EstateType.Truplanning) {
      isPending =
        !estate.estateTruplanningFields?.clientFirstName ||
        !estate.estateTruplanningFields?.clientLastName;
    } else if (estate.type === EstateType.Truproxy) {
      isPending =
        !estate.estateTruproxyFields?.wardFirstName || !estate.estateTruproxyFields?.wardLastName;
    }
    if (!isPending) {
      isPending = estateInputDataInstances.some(
        (estateInputDataInstance) =>
          !estateInputDataInstance.data &&
          estateInputDataInstance.estateInputDataFormat?.type === EstateInputDataFormatType.Data,
      );
    }

    return isPending;
  }

  const docEstateInputDataInstances = estateInputDataInstances.filter(
    (estateInputDataInstance) =>
      estateInputDataInstance.estateInputDataFormat?.type === EstateInputDataFormatType.Document,
  );

  const documents = getFilesData?.data || [];
  const isRequiredDocumentFieldPending =
    docEstateInputDataInstances.some(
      (docEstateInputDataInstances) => !docEstateInputDataInstances.data,
    ) || documents.some((document) => document.isFilePendingSignature);

  const isMatterInfoRequiredFieldPending = getIsMatterInfoRequiredFieldPending();

  const isSelfService =
    estateWorkItemData?.data &&
    estateWorkItemData!.data!.autoDocumentGeneration &&
    (estateWorkItemData!.data!.documentGenerationTemplateId ||
      estateWorkItemData!.data!.conditionalTemplateQuestionSetId);

  useEffect(() => {
    if (isSelfService) {
      setCurrentTab(AssignedTaskDrawerTabs.TASK_ACTIVITY);
      return;
    }

    if (isMatterInfoRequiredFieldPending) {
      setCurrentTab(AssignedTaskDrawerTabs.MATTER_INFORMATION);
    }
  }, [isMatterInfoRequiredFieldPending, isSelfService]);

  return (
    <Drawer
      anchor="right"
      open={true}
      onClose={onClose}
      PaperProps={{
        sx: { width: '880px' },
      }}
    >
      <div className={classes.container}>
        <div className={classes.header}>
          <Typography className={classes.title}>{estate.estateName}</Typography>
          <IconButton onClick={onClose}>
            <LastPageIcon className={classes.lastPageIcon} />
          </IconButton>
        </div>
      </div>
      {isLoading && <Spinner />}
      {!isLoading && (
        <>
          <Box className={classes.tabsContainer}>
            <Tabs
              value={currentTab}
              onChange={(_: React.SyntheticEvent, newValue: number) => {
                setCurrentTab(newValue);
              }}
              textColor="inherit"
            >
              <Tab label="Task Activity" />
              <Tab
                label="Matter Information"
                icon={
                  isMatterInfoRequiredFieldPending ? (
                    <CircleIcon className={classes.actionNeededCircleIcon} />
                  ) : undefined
                }
                iconPosition="end"
                className={isSelfService ? classes.hidden : ''}
              />

              <Tab
                label="Documents"
                icon={
                  isRequiredDocumentFieldPending ? (
                    <CircleIcon className={classes.actionNeededCircleIcon} />
                  ) : undefined
                }
                iconPosition="end"
              />
              <Tab label="Notes" />
            </Tabs>
          </Box>
          {currentTab === AssignedTaskDrawerTabs.TASK_ACTIVITY && (
            <AssignedTaskActivity estateWorkItem={estateWorkItemData!.data as EstateWorkItem} />
          )}
          {currentTab === AssignedTaskDrawerTabs.MATTER_INFORMATION && (
            <AssignedTaskMatterInfo
              estateWorkItem={estateWorkItemData!.data as EstateWorkItem}
              estateInputDataInstances={estateInputDataInstances}
              refetchEstateInputDataInstances={refetchEstateInputDataInstances}
              onClose={onClose}
            />
          )}
          {currentTab === AssignedTaskDrawerTabs.DOCUMENTS && (
            <AssignedTaskDocuments
              documents={getFilesData!.data}
              estate={estate}
              estateInputDataInstances={docEstateInputDataInstances}
              onRefreshDocuments={refetchGetFilesData}
              refetchEstateInputDataInstances={refetchEstateInputDataInstances}
            />
          )}
          {currentTab === AssignedTaskDrawerTabs.NOTES && (
            <AssignedTaskNotes
              estateWorkItemId={estateWorkItemData!.data.id!}
              portalId={portalId}
            />
          )}
        </>
      )}
    </Drawer>
  );
};

export default AssignedTaskDrawer;
